/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css, keyframes } from "styled-components";
import Icon from "./Icon";
import { useTranslate } from "../../hooks/useTranslate";

export const buttonBaseStyles = css`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  text-transform: none;
  padding: 0;
  border-radius: 8px;
  font-family: inherit;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border-style: solid;
  border-width: 1px;
  border-color: transparent;
  background: none;
`;

export const buttonVariantStyles = {
  primary: css`
    font-weight: 600;
    background: rgba(${({ theme }) => theme.colors.accent.rgba});
    color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});

    svg {
      stroke: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
    }

    &.active {
      background-color: rgba(${({ theme }) => theme.colors.accent.rgba});
    }

    @media (hover: hover) {
      &:hover {
        background-color: rgba(${({ theme }) => theme.colors.accent.rgba});
      }
    }
  `,
  secondary: css`
    //font-weight: 600;
    background: transparent;
    color: rgba(${({ theme }) => theme.colors.text.rgba});
    border-color: rgba(${({ theme }) => theme.colors.accent.rgba});

    svg {
      stroke: rgba(${({ theme }) => theme.colors.accent.rgba});
    }

    &.active {
      color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
      background-color: rgba(${({ theme }) => theme.colors.accent.rgba});

      svg {
        stroke: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
      }
    }

    @media (hover: hover) {
      &:hover {
        color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
        background-color: rgba(${({ theme }) => theme.colors.accent.rgba});

        svg {
          stroke: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
        }
      }
    }
  `,
  outline: css`
    font-weight: 400;
    background: rgba(${({ theme }) => theme.colors.background.rgba});
    color: rgba(${({ theme }) => theme.colors.text.rgba});
    border-color: rgba(${({ theme }) => theme.colors.border.rgba});

    svg {
      stroke: rgba(${({ theme }) => theme.colors.text.rgba});
    }

    &.active {
      background: rgba(${({ theme }) => theme.colors.accent.rgba});
      color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
      border-color: rgba(${({ theme }) => theme.colors.accent.rgba});

      svg {
        stroke: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
      }
    }

    @media (hover: hover) {
      &:hover {
        background: rgba(${({ theme }) => theme.colors.accent.rgba});
        color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
        border-color: rgba(${({ theme }) => theme.colors.accent.rgba});

        svg {
          stroke: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
        }
      }
    }
  `,
  transparent: css`
    font-weight: 400;
    background-color: transparent;
    background: transparent;
    color: rgba(${({ theme }) => theme.colors.text.rgba});

    &.active {
      background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.15);
    }

    @media (hover: hover) {
      &:hover {
        background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.15);
      }
    }
  `,

  danger: css`
    font-weight: 400;
    background-color: transparent;
    background: transparent;
    color: rgba(${({ theme }) => theme.colors.error.rgba});
    border: none;

    svg {
      stroke: rgba(${({ theme }) => theme.colors.error.rgba});
    }

    &.active {
      background: rgba(${({ theme }) => theme.colors.error.rgb}, 0.1);
    }

    @media (hover: hover) {
      &:hover {
        background: rgba(${({ theme }) => theme.colors.error.rgb}, 0.1);
      }
    }
  `,
  dangerOutline: css`
    font-weight: 400;
    background-color: transparent;
    background: transparent;
    color: rgba(${({ theme }) => theme.colors.text.rgba});
    border-color: rgba(${({ theme }) => theme.colors.border.rgba});

    svg {
      stroke: rgba(${({ theme }) => theme.colors.error.rgba});
    }

    &.active {
      background: rgba(${({ theme }) => theme.colors.error.rgb}, 0.1);
    }

    @media (hover: hover) {
      &:hover {
        background: rgba(${({ theme }) => theme.colors.error.rgb}, 0.1);
      }
    }
  `,
};

export const buttonSizeStyles = {
  l: css`
    min-height: 2.625rem;
    max-height: 2.625rem;
    padding-inline: 32px;
    font-size: ${({ theme }) => theme.fontSizes.p};
    line-height: 1;
  `,
  m: css`
    min-height: 2.25rem;
    max-height: 2.25rem;
    padding-inline: 24px;
    font-size: calc(${({ theme }) => theme.fontSizes.p} * 0.875);
    line-height: 1;
  `,
  icon: css`
    width: 2.25rem;
    height: 2.25rem;
  `,
  iconLarge: css`
    width: 2.625rem;
    height: 2.625rem;
  `,
};

const StyledButton = styled.button`
  ${buttonBaseStyles}
  position: relative;
  ${(props) => buttonVariantStyles[props.$variant]}
  ${(props) => buttonSizeStyles[props.$size]}
  ${(props) =>
    props.$fullWidth &&
    css`
      width: 100%;
      flex: 1;
    `}
  ${(props) => props.disabled && "pointer-events: none; opacity: 0.5;"}
  ${(props) => props.$loading && "pointer-events: none;"}
`;

const StyledButtonInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
`;

const buttonSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const buttonSpinReduced = keyframes`
  50% { opacity: 0.4; }
`;

const ButtonSpinner = styled.svg`
  width: ${(props) => props.$size};
  height: ${(props) => props.$size};
  animation: ${buttonSpin} 0.75s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    && {
      animation: ${buttonSpinReduced} 1.5s ease-in-out infinite !important;
    }
  }

  circle {
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
    cx: 12;
    cy: 12;
    r: 10;
    stroke-dasharray: 45 63;
    stroke-linecap: round;
  }
`;

const ButtonLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonCount = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.25rem;
  border-radius: 50px;
  background: rgba(${({ theme }) => theme.colors.accent.rgba});
  color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
  font-size: 0.625rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const Button = React.forwardRef(
  (
    { title, icon, variant = "primary", children, size = "l", fullWidth, loading, isActive, disabled, href, target, rel, count, ...props },
    ref
  ) => {
    const { _t } = useTranslate();
    const shouldShowLoadingInsteadOfIcon = loading && (size === "icon" || size === "iconLarge");
    const isLink = !!href;

    const loadingIndicator = (
      <ButtonSpinner $size={Icon.SIZE.SM} viewBox="0 0 24 24">
        <circle />
      </ButtonSpinner>
    );
    const iconWithDefaultSize =
      icon && React.isValidElement(icon) && icon.props.size === undefined
        ? React.cloneElement(icon, { size: Icon.SIZE.XS })
        : icon;
    const displayIcon = shouldShowLoadingInsteadOfIcon ? loadingIndicator : iconWithDefaultSize;
    const showLoadingOverlay = loading && !shouldShowLoadingInsteadOfIcon;

    const linkProps = isLink
      ? {
        href,
        target,
        rel: rel || (target === "_blank" ? "noopener noreferrer" : undefined),
      }
      : {};

    return (
      <StyledButton
        ref={ref}
        as={isLink ? "a" : "button"}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $loading={loading}
        className={isActive ? "active" : ""}
        disabled={disabled}
        {...linkProps}
        {...props}
      >
        <StyledButtonInner $hidden={showLoadingOverlay}>
          {displayIcon && displayIcon}
          {_t(title)}
        </StyledButtonInner>
        {showLoadingOverlay && (
          <ButtonLoadingOverlay>
            {loadingIndicator}
          </ButtonLoadingOverlay>
        )}
        {children}
        {!!count && <ButtonCount>{count}</ButtonCount>}
      </StyledButton>
    );
  }
);

export default Button;
